import { Component, forwardRef, input, output, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Icon } from '@components/abb-icon/icons';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { TPipe } from '@pipes/t.pipe';

export interface DataCard<Id> {
  id: Id;
  icon?: Icon;
  title: string;
  description: string;
  editable?: boolean;
}

@Component({
  selector: 'abb-mini-card-selection',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AbbMiniCardSelectionComponent),
      multi: true,
    },
  ],
  template: `
    <div
      class="grid px-[0.1rem]"
      [ngClass]="customGridClass() ? customGridClass() : 'grid-cols-3 gap-6'"
    >
      @for(option of options(); track option.id){

      <a class="relative z-0" href="#" (click)="onCardSelect($event, option)">
        <mat-card
          [id]="option.id"
          class="mat-card max-w-auto w-36 max-h-auto h-32 overflow-hidden relative z-0"
          [ngStyle]="
            option.id === value()
              ? { border: '2px solid rgb(var(--abb-color-primary))' }
              : {}
          "
          fxFlexAlign="stretch"
          appearance="raised"
        >
          @if (option.editable) {
          <div class="absolute z-10 top-3.5 right-2.5 flex gap-3">
            <abb-button
              (onclick)="onDelete($event, option.id)"
              variant="clean"
              [icon]="'mat/delete'"
              iconSize="small"
            />
            <abb-button
              (onclick)="onClone($event, option.id)"
              variant="clean"
              [icon]="'mat/file_copy'"
              iconSize="small"
            />
            <abb-button
              (onclick)="onEdit($event, option.id)"
              variant="clean"
              [icon]="'mat/edit'"
              iconSize="small"
            />
          </div>
          }
          <mat-card-header class="mat-card-header">
            <mat-card-subtitle class="mat-card-subtitle">
              <div class="flex flex-col gap-2 w-full">
                @if (option.icon) {
                <abb-icon
                  class="text-abb-font"
                  [name]="option.icon"
                  [size]="'small'"
                ></abb-icon>
                }
                <span
                  class="line-clamp-1 max-w-full text-abb-font font-medium break-words"
                  overflowTooltip
                  [useClampMode]="true"
                  >{{ option.title }}</span
                >
              </div>
            </mat-card-subtitle>
          </mat-card-header>
          <mat-card-content class="mat-card-content">
            <span
              class="line-clamp-3 max-w-full text-abb-font break-words"
              overflowTooltip
              [useClampMode]="true"
            >
              {{ option.description }}
            </span>
          </mat-card-content>
        </mat-card>
      </a>
      }
      <a href="#" (click)="addCard($event)">
        <mat-card
          id="add-card"
          class="mat-card max-w-auto w-36 max-h-auto h-32 overflow-hidden relative z-0"
          [ngStyle]="{ border: '2px dashed rgb(var(--abb-color-primary)/0.4)' }"
          fxFlexAlign="stretch"
          appearance="raised"
        >
          <mat-card-header class="mat-card-header">
            <mat-card-subtitle class="mat-card-subtitle">
              <div class="flex flex-col gap-2 w-full">
                <span
                  class="inline-block truncate max-w-full text-abb-font grow"
                  >{{ 'Customizable' | t }}</span
                >
              </div>
            </mat-card-subtitle>
          </mat-card-header>
          <mat-card-content
            class="mat-card-content flex flex-col items-center justify-center h-full"
          >
            <abb-icon
              class="text-abb-primary/40 h-full items-center justify-center"
              [name]="'mat/add_circle'"
              [externalSize]="'text-5xl'"
            ></abb-icon>
          </mat-card-content>
        </mat-card>
      </a>
    </div>
  `,
  styles: `
        .mat-card {
            box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
        }
        .mat-card-header {
            padding: 12px 12px 0;
        }
        .mat-card-subtitle {
            font-size: 0.875rem;
            line-height: 1.25rem;
        }
        .mat-card-content {
            font-size: 0.75rem;
            line-height: 1rem;
            padding-top: 0.5rem;
            padding-left: 12px;
            padding-right: 12px;
        }
        .mat-card-content:last-child {
            padding-bottom: 12px;
        }
    `,
  host: {
    '[style.opacity]': 'opacity',
  },
  imports: [
    CommonModule,
    MatCardModule,
    AbbIconComponent,
    AbbButtonComponent,
    OverflowTooltipDirective,
    TPipe,
  ],
})
export class AbbMiniCardSelectionComponent<Id> implements ControlValueAccessor {
  readonly customGridClass = input<string | undefined>(undefined);
  readonly options = input<Array<DataCard<Id>>>([]);
  addNewTemplate = output<void>();
  name = input<string>('');
  required = signal(false);
  value = signal<Id | null>(null);
  disabled = false;
  onChange = (_v: Id) => {};
  onTouched = () => {};
  edit = output<Id>();
  clone = output<Id>();
  delete = output<Id>();
  onCardSelected = output<DataCard<Id>>();
  get opacity(): number {
    return this.disabled ? 0.25 : 1;
  }

  onCardSelect(event: MouseEvent, card: DataCard<Id>): void {
    event.preventDefault();
    if (!this.disabled) {
      this.writeValue(card.id);
      this.onCardSelected.emit(card);
    }
  }

  addCard(event: MouseEvent): void {
    event.preventDefault();
    this.addNewTemplate.emit();
  }

  writeValue(id: Id): void {
    this.value.set(id);
    this.onChange(id);
    this.onTouched();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  onEdit(e: Event, id: Id) {
    e.preventDefault();
    e.stopPropagation();
    this.edit.emit(id);
  }
  onClone(e: Event, id: Id) {
    e.preventDefault();
    e.stopPropagation();
    this.clone.emit(id);
  }
  onDelete(e: Event, id: Id) {
    e.preventDefault();
    e.stopPropagation();
    this.delete.emit(id);
  }
}
